import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';
import { useCurrentLocale } from 'contexts/CurrentLocaleContext';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useIsFirstRenderRef } from 'hooks/useIsFirstRenderRef';
import { useMultipleQueryParamStates } from 'hooks/useMultipleQueryParamStates';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import { useTrackPageView } from 'hooks/useTrackPageView';
import { homeRoute } from 'routing/routes';
import { allowRedirect } from 'utils/allowRedirect';

import { LoginPage } from './LoginPage';

export function LoginPageContainer() {
  const location = useLocation<{
    savedListing?: { id: string; type: string; name: string };
    subscribedSubsite?: string;
    title?: string;
    inviteId?: string;
    email?: string;
    redirectTo?: string;
  }>();

  const navigate = useNavigate();

  const { currentLocale } = useCurrentLocale();
  const { user } = useUser();

  const { isFirstRenderRef } = useIsFirstRenderRef();
  useEffect(() => {
    // Redirect to home if the user lands on the login page while logged in
    if (isFirstRenderRef.current && user)
      void navigate(homeRoute.with({ lang: currentLocale }));
  }, [currentLocale, isFirstRenderRef, navigate, user]);

  const [stateFromQuery] = useMultipleQueryParamStates({
    names: ['invite', 'to'],
  });

  useSetDocumentTitle(location.state?.title);

  useTrackPageView('Login', {
    page_type: 'Login',
  });

  const handleRedirect = useCallback(
    (user: ApiUser) => {
      const inviteId = location.state?.inviteId;

      let redirectTo = null;

      if (inviteId) {
        redirectTo = `/user-invites/${inviteId}`;
      } else {
        redirectTo = stateFromQuery.to ?? location.state?.redirectTo;
      }

      if (!redirectTo || !allowRedirect(redirectTo)) {
        redirectTo = `/${user.locale}/`;
      }

      if (user.locale !== CURRENT_LOCALE()) {
        const regexp = new RegExp(`^/${CURRENT_LOCALE()}`);
        redirectTo = redirectTo.replace(regexp, `/${user.locale}`);
        void navigate(redirectTo);
      }

      void navigate(redirectTo);
    },
    [stateFromQuery, location, navigate],
  );

  return (
    <LoginPage
      onLogin={handleRedirect}
      redirectTo={stateFromQuery.to ?? location.state?.redirectTo}
      email={location.state?.email}
      inviteId={stateFromQuery.invite ?? location.state?.inviteId}
      savedListing={location.state?.savedListing}
      subscribedSubsite={location.state?.subscribedSubsite}
      title={location.state?.title}
    />
  );
}
